import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const AgentStatusPanel = ({ isOpen = false, onToggle }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const agentRoles = [
    {
      id: 'coordinator',
      name: 'Coordinador Principal',
      role: 'Coordinación de Flujo',
      icon: 'Network',
      color: 'var(--color-primary)'
    },
    {
      id: 'pdd-drafter',
      name: 'Redactor PDD',
      role: 'Documentación Técnica',
      icon: 'FileEdit',
      color: 'var(--color-accent)'
    },
    {
      id: 'mrv-specialist',
      name: 'Especialista MRV',
      role: 'Monitoreo y Verificación',
      icon: 'BarChart3',
      color: 'var(--color-success)'
    },
    {
      id: 'geospatial',
      name: 'Analista Geoespacial',
      role: 'Análisis Territorial',
      icon: 'Map',
      color: 'var(--color-warning)'
    },
    {
      id: 'regulatory',
      name: 'Experto Art. 6',
      role: 'Cumplimiento Regulatorio',
      icon: 'Scale',
      color: 'var(--color-secondary)'
    }
  ];

  useEffect(() => {
    fetchAgentStatus();
    const interval = setInterval(fetchAgentStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAgentStatus = async () => {
    setLoading(true);
    try {
      const mockAgents = agentRoles?.map(role => ({
        ...role,
        status: Math.random() > 0.3 ? 'available' : Math.random() > 0.5 ? 'busy' : 'offline',
        activeProjects: Math.floor(Math.random() * 5),
        lastActivity: new Date(Date.now() - Math.random() * 3600000)?.toISOString()
      }));
      
      setAgents(mockAgents);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching agent status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'var(--color-success)';
      case 'busy':
        return 'var(--color-warning)';
      case 'offline':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available':
        return 'Disponible';
      case 'busy':
        return 'Ocupado';
      case 'offline':
        return 'Desconectado';
      default:
        return 'Desconocido';
    }
  };

  const formatLastActivity = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 1) return 'Ahora mismo';
    if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return date?.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      <button
        onClick={onToggle}
        className="lg:hidden fixed bottom-6 right-6 z-900 flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-250 focus-ring"
        aria-label="Toggle agent panel"
      >
        <Icon name={isOpen ? 'X' : 'Users'} size={24} />
      </button>
      <aside
        className={`
          fixed lg:sticky top-16 right-0 h-[calc(100vh-4rem)] w-80 bg-card border-l border-border
          transition-transform duration-250 z-900 overflow-hidden flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={20} color="var(--color-primary)" />
            <h2 className="font-semibold text-lg font-mono">Estado de Agentes</h2>
          </div>
          <button
            onClick={fetchAgentStatus}
            disabled={loading}
            className="flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-250 focus-ring disabled:opacity-50"
            aria-label="Refresh agent status"
          >
            <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && agents?.length === 0 ? (
            <div className="space-y-4">
              {[...Array(5)]?.map((_, i) => (
                <div key={i} className="skeleton h-24 rounded-md" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {agents?.map((agent) => (
                <div
                  key={agent?.id}
                  className="p-4 bg-background rounded-md border border-border hover:border-primary/20 transition-all duration-250"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-lg"
                      style={{ backgroundColor: `${agent?.color}15` }}
                    >
                      <Icon name={agent?.icon} size={20} color={agent?.color} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-sm text-foreground truncate">
                          {agent?.name}
                        </h3>
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getStatusColor(agent?.status) }}
                          title={getStatusLabel(agent?.status)}
                        />
                      </div>
                      
                      <p className="text-xs text-muted-foreground caption mb-2">
                        {agent?.role}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground caption">
                          {getStatusLabel(agent?.status)}
                        </span>
                        {agent?.activeProjects > 0 && (
                          <span className="text-muted-foreground caption">
                            {agent?.activeProjects} {agent?.activeProjects === 1 ? 'proyecto' : 'proyectos'}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-xs text-muted-foreground caption mt-1">
                        {formatLastActivity(agent?.lastActivity)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs caption text-muted-foreground">
            <span>Última actualización</span>
            <span>{lastUpdate?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AgentStatusPanel;